import { Screen, LocalStorage } from "quasar"; // Assurez-vous que Quasar est importé
import { useAuthUnifiedStore } from "../stores/authUnified.js";

export function mobileRedirect(to, from, next) {
  // SSR : ne pas rediriger (Screen non fiable sans viewport) → servir / par défaut
  if (typeof window === "undefined") return next();

  const isMobile = Screen.sm || Screen.xs;
  const authStore = useAuthUnifiedStore();
  if (to.path === "/suppression" || to.path === "/recherche-carte") {
    return next();
  }

  if (to.path === "/confirm-account-deletion") {
    return next();
  }
  // Si c'est la page CGU, toujours autoriser
  if (to.path === "/cgu") {
    return next();
  }

  // ⚠️ CRITIQUE : Si on revient d'une auth OAuth (Google, Apple), NE PAS rediriger
  // pour laisser IndexPage traiter le paramètre PROVIDER
  if (to.query.PROVIDER && ['google', 'apple'].includes(to.query.PROVIDER.toLowerCase())) {
    return next();
  }

  // Si on est déjà sur mobile et qu'on est mobile, continuer
  if (to.path === "/mobile" && isMobile) {
    return next();
  }

  // Si on est sur la page d'accueil et qu'on est desktop, continuer
  if (to.path === "/" && !isMobile) {
    return next();
  }

  /* // Si l'utilisateur n'est pas un hôte
  if (!store.isHost && to.path !== "/mobile") {
    if (isMobile) {
      return next("/mobile");
    } else {
      return next("/");
    }
  }

  // Si c'est mobile et qu'on n'est pas déjà sur mobile, rediriger
  */

  // Si c'est mobile et qu'on n'est pas déjà sur mobile, rediriger
  // mais préserver les query params
  if (isMobile && to.path !== "/mobile") {
    return next({
      path: "/mobile",
      query: to.query // ✅ Préserver les query params
    });
  }

  // Sinon, continuer vers la route demandée
  return next();
}

/**
 * Guard pour vérifier que l'utilisateur connecté est un hôte
 * Redirige vers la page d'accueil si ce n'est pas le cas
 */
export function hostOnlyGuard(to, from, next) {
  const authStore = useAuthUnifiedStore();
  const isMobile = Screen.sm || Screen.xs;

  if (!authStore.isAuthenticated) {
    return next(isMobile ? "/mobile" : "/");
  }

  if (!authStore.isHost) {
    return next(isMobile ? "/mobile" : "/");
  }

  return next();
}

/**
 * Guard pour vérifier que l'utilisateur connecté est un démarcheur
 * Redirige vers la page d'accueil si ce n'est pas le cas
 */
export function demarcheurOnlyGuard(to, from, next) {
  const authStore = useAuthUnifiedStore();
  const isMobile = Screen.sm || Screen.xs;

  if (!authStore.isAuthenticated) {
    return next(isMobile ? "/mobile" : "/");
  }

  if (!authStore.isDemarcheur) {
    return next(isMobile ? "/mobile" : "/");
  }

  return next();
}

/**
 * Guard pour vérifier que l'utilisateur connecté est un voyageur
 * Redirige vers la page d'accueil si ce n'est pas le cas
 */
export function voyageurOnlyGuard(to, from, next) {
  const authStore = useAuthUnifiedStore();
  const isMobile = Screen.sm || Screen.xs;

  if (!authStore.isAuthenticated) {
    return next(isMobile ? "/mobile" : "/");
  }

  if (!authStore.isVoyageur) {
    return next(isMobile ? "/mobile" : "/");
  }

  return next();
}

/**
 * Guard pour vérifier que l'utilisateur est authentifié
 * Initialise le store depuis le storage si nécessaire
 * Redirige vers la page de login si non authentifié
 */
export function requireAuthGuard(to, from, next) {
  const authStore = useAuthUnifiedStore();
  const isMobile = Screen.sm || Screen.xs;

  // Initialiser depuis le storage si pas déjà fait
  if (!authStore.isAuthenticated || !authStore.user?.id) {
    authStore.initializeFromStorage(false);
  }

  // Vérifier l'authentification après initialisation
  if (!authStore.isAuthenticated || !authStore.user?.id) {
    return next(isMobile ? "/mobile/authentification" : "/");
  }

  return next();
}

export function desktopRedirect(to, from, next) {
  // SSR : ne pas rediriger (Screen non fiable sans viewport)
  if (typeof window === "undefined") return next();

  const isDesktop = !Screen.sm && !Screen.xs;
  const authStore = useAuthUnifiedStore();
  if (to.path.startsWith("/mobile/facturation") && isDesktop) {
    if (authStore.isAuthenticated && authStore.isVoyageur) {
      return next(`/voyageur?reservation=${to.params.reservationId}`);
    }
    if (authStore.isAuthenticated && authStore.isHost) {
      return next(`/hotes/reservation?reservation=${to.params.reservationId}`);
    }
  }

  if (to.path === "/suppression") {
    return next();
  }
  if (to.path === "/confirm-account-deletion") {
    return next();
  }
  // Si c'est la page CGU, toujours autoriser
  if (to.path === "/cgu") {
    return next();
  }

  // Si on est déjà sur la page d'accueil et qu'on est desktop, continuer
  if (to.path === "/" && isDesktop) {
    return next();
  }

  // Si on est sur mobile et qu'on est mobile, continuer
  if (to.path.startsWith("/mobile") && !isDesktop) {
    return next();
  }

  // Si l'utilisateur n'est pas un hôte
  if (!authStore.isHost) {
    if (isDesktop) {
      return next("/");
    } else {
      return next("/mobile");
    }
  }

  // Si c'est desktop et qu'on n'est pas déjà sur la page d'accueil, rediriger
  if (isDesktop && to.path !== "/") {
    return next("/");
  }

  // Sinon, continuer vers la route demandée
  return next();
}

const viewDetailsResidence = () =>
  import("../components/public/viewDetailsResidence.vue");

const mobileIndexComponent = () =>
  import("src/components/reservation/mobileIndexComponent.vue");

const detailsMobilePage = () => import("src/pages/detailsMobilePage.vue");

const OfflinePage = () => import("src/pages/OfflinePage.vue");
const VerifyEmailPage = () => import("src/pages/EmailVerifyPage.vue");
const MonCompteComponent = () =>
  import("components/hotes/monCompteComponent.vue");
const redirectPage = () => import("src/pages/redirectMobilePage.vue");
// const IndexPage = defineAsyncComponent(() => import("../pages/IndexPage.vue"));
const IndexPage = () => import("../pages/IndexPage.vue");
const routes = [
  {
    path: "/",
    component: () => import("layouts/FullLayout.vue"),
    beforeEnter: mobileRedirect, // Ajoutez le middleware ici
    meta: { requiresConnection: true }, // Route nécessitant Internet

    children: [
      {
        path: "recherche-carte",
        name: "mobile-search-map",
        component: () => import("pages/SearchMapPage.vue"),
      },
      {
        path: "",
        component: IndexPage,
        // component: () => import("pages/assistancePage.vue"),
      },

      {
        path: "confirm",
        component: () => import("pages/reservationConfirmPage.vue"),
      },

      {
        path: "cgu",
        component: () =>
          import(/* webpackChunkName: "assistance" */ "pages/cguPage.vue"),
      },
      {
        path: "politique_confidentialite",
        component: () =>
          import(
            /* webpackChunkName: "assistance" */ "pages/confidentialitePage.vue"
          ),
      },
      {
        path: "info_entreprise",
        component: () =>
          import(
            /* webpackChunkName: "assistance" */ "pages/infoEntreprisePage.vue"
          ),
      },
      {
        path: "assistance",
        component: () =>
          import(
            /* webpackChunkName: "assistance" */ "pages/assistancePage.vue"
          ),
      },
      {
        path: "suppression",
        component: () => import("pages/suppressionPage.vue"),
      },
      {
        path: "confirm-account-deletion",
        component: () => import("pages/ConfirmDeletionPage.vue"),
      },
    ],
  },
  {
    path: "/password/reset",
    component: () => import("src/layouts/FullLayout.vue"),
    meta: { requiresConnection: true }, // Route nécessitant Internet
    children: [
      { path: "", component: () => import("pages/ResetPasswordPage.vue") },
    ],
  },
  {
    path: "/offline",
    name: "Offline",
    component: () => import("src/layouts/FullLayout.vue"),
    children: [{ path: "", name: "OfflinePage", component: OfflinePage }], // Page pour les utilisateurs hors ligne
  },
  {
    path: "/verify-email",
    name: "verify-email",
    component: VerifyEmailPage,
  },
  {
    path: "/redirect",
    component: () => import("src/layouts/FullLayout.vue"),
    children: [{ path: "", component: redirectPage }],
  },
  {
    meta: { requiresConnection: true }, // Route nécessitant Internet
    path: "/hotes",
    component: () => import("src/layouts/HoteLayout.vue"),
    beforeEnter: hostOnlyGuard, // Vérification du rôle hôte
    children: [
      {
        path: "",
        component: () => import("src/components/hotes/indexComponent.vue"),
      },
      {
        path: "meslogements",
        component: () =>
          import("src/components/hotes/listLogementComponent.vue"),
      },

      {
        path: "reservation",
        component: () =>
          import("src/components/hotes/listReservationComponent.vue"),
      },
      {
        path: "monCompte",
        component: MonCompteComponent,
      },
      {
        path: "fonds",
        component: () => import("src/components/hotes/fondsComponent.vue"),
      },
    ],
  },

  {
    path: "/demarcheur",
    component: () => import("src/layouts/DemarcheurLayout.vue"),
    beforeEnter: demarcheurOnlyGuard, // Vérification du rôle démarcheur

    children: [
      {
        path: "",
        component: () => import("src/components/demarcheur/indexComponent.vue"),
      },

      {
        path: "logements",
        component: () => import("src/components/demarcheur/listLogement.vue"),
      },
      {
        path: "fonds",
        component: () => import("src/components/hotes/fondsComponent.vue"),
      },
      {
        path: "reservation",
        component: () =>
          import("src/components/hotes/listReservationComponent.vue"),
      },
      {
        path: "moncompte",
        component: MonCompteComponent,
      },
      {
        path: "simulateur",
        redirect: "simulateur/manuel",
      },
      {
        path: "simulateur/manuel",
        name: "demarcheur-simulateur-manuel",
        component: () => import("src/pages/demarcheurSimulateurManuelPage.vue"),
      },
      {
        path: "simulateur/objectif",
        name: "demarcheur-simulateur-objectif",
        component: () =>
          import("src/pages/demarcheurSimulateurObjectifPage.vue"),
      },
    ],
  },
  {
    path: "/voyageur",
    component: () => import("src/layouts/VoyageurLayout.vue"),
    beforeEnter: voyageurOnlyGuard, // Vérification du rôle voyageur

    children: [
      {
        path: "",
        component: () =>
          import("src/components/hotes/listReservationComponent.vue"),
      },
      {
        path: "favoris",
        component: () => import("pages/FavoritePage.vue"),
      },
      {
        path: "monCompte",
        component: () => import("pages/monComptePage.vue"),
      },
    ],
  },
  {
    path: "/login",
    component: () => import("src/layouts/FullLayout.vue"),
    children: [
      { path: "", component: () => import("pages/AuthenttificatePage.vue") },
    ],
  },

  {
    path: "/iphone",
    component: () => import("src/layouts/iphoneLayout.vue"),
    children: [
      {
        path: "",
        component: () =>
          import("src/components/public/viewDetailsResidenceIphone.vue"),
      },
    ],
  },
  {
    path: "/mobile/signaler-probleme/:id",
    component: () => import("src/layouts/FullLayout.vue"),
    children: [
      {
        path: "",
        name: "SignalerProblemePage",
        component: () => import("src/pages/SignalerProblemePage.vue"),
      },
    ],
  },
  {
    path: "/mobile",
    component: () => import("src/layouts/mobileLayout.vue"),
    beforeEnter: desktopRedirect, // Ajoutez le middleware ici
    children: [
      {
        path: "suppression",
        component: () => import("pages/suppressionPage.vue"),
      },
      {
        path: "facturation/:reservationId",
        component: () => import("pages/FacturationPage.vue"),
      },

      {
        path: "",
        component: () => import("pages/IndexPage.vue"),
        // component: () => import("pages/assistancePage.vue"),
      },

      {
        path: "cgu",
        component: () => import("pages/cguPage.vue"),
      },
      {
        path: "assistance",
        component: () => import("pages/assistancePage.vue"),
      },
      {
        path: "fonds",
        redirect: "/mobile/fonds/solde",
        component: () => import("src/pages/FondsRouterWrapper.vue"),
        beforeEnter: requireAuthGuard,
        children: [
          {
            path: "solde",
            name: "mobile-fonds-solde",
            component: () => import("src/pages/fondsSoldePage.vue"),
          },
          {
            path: "gains",
            name: "mobile-fonds-gains",
            component: () => import("src/pages/fondsGainsPage.vue"),
          },
          {
            path: "reservations",
            name: "mobile-fonds-reservations",
            component: () => import("src/pages/fondsReservationsPage.vue"),
          },
        ],
      },
      {
        path: "login",
        component: () => import("pages/MobileAuthenttificatePage.vue"),
      },
      {
        path: "reservations",
        component: () =>
          import("src/components/hotes/listReservationComponent.vue"),
      },
      {
        path: "favorites",
        component: () => import("pages/FavoritePage.vue"),
      },
      {
        path: "voyageur",
        component: () => import("pages/IndexPage.vue"),
      },
      {
        path: "hotes",
        component: () => import("src/components/hotes/indexComponent.vue"),
      },
      {
        path: "logements",
        component: () => import("src/pages/listLogementPage.vue"),
      },
      {
        path: "moncompte",
        component: () => import("components/monCompteMobileComponent.vue"),
      },
      {
        path: "residence",
        redirect: "/mobile/residence/logements",
        component: () => import("src/pages/ResidenceRouterWrapper.vue"),
        beforeEnter: demarcheurOnlyGuard,
        children: [
          {
            path: "logements",
            name: "mobile-residence-logements",
            component: () => import("src/pages/demarcheurLogementsPage.vue"),
          },
          {
            path: "hotes",
            name: "mobile-residence-hotes",
            component: () => import("src/pages/demarcheurPage.vue"),
          },
        ],
      },
      {
        path: "demarcheur",
        component: () => import("src/components/demarcheur/indexComponent.vue"),
      },
      {
        path: "demarcheur/simulateur",
        redirect: "demarcheur/simulateur/manuel",
      },
      {
        path: "demarcheur/simulateur/manuel",
        name: "mobile-demarcheur-simulateur-manuel",
        component: () => import("src/pages/demarcheurSimulateurManuelPage.vue"),
        beforeEnter: demarcheurOnlyGuard,
      },
      {
        path: "demarcheur/simulateur/objectif",
        name: "mobile-demarcheur-simulateur-objectif",
        component: () =>
          import("src/pages/demarcheurSimulateurObjectifPage.vue"),
        beforeEnter: demarcheurOnlyGuard,
      },
      {
        path: "redirect",
        component: () => import("src/layouts/FullLayout.vue"),
        children: [{ path: "", component: redirectPage }],
      },
    ],
  },
  {
    path: "/logement/:logementId",
    component: () => import("src/layouts/FullLayout.vue"),
    children: [
      { path: "", component: viewDetailsResidence, props: true },
      { path: "reserve", component: mobileIndexComponent, props: true },
      { path: "details", component: detailsMobilePage, props: true },
      {
        path: "confirm",
        component: () => import("pages/reservationConfirmPage.vue"),
        props: true,
      },
    ],
  },
  {
    path: "/mobile/versement/:id",
    component: () => import("src/layouts/FullLayout.vue"),
    children: [
      {
        path: "",
        component: () => import("src/pages/fondDescriptionPage.vue"),
      },
    ],
  },
  {
    path: "/mobile/fonds/description/:id",
    component: () => import("src/layouts/FullLayout.vue"),
    children: [
      {
        path: "",
        name: "mobile-fonds-description",
        component: () => import("src/pages/fondDescriptionPage.vue"),
      },
    ],
  },
  {
    path: "/mobile/signaler-probleme/:id",
    component: () => import("src/layouts/FullLayout.vue"),
    children: [
      {
        path: "",
        name: "SignalerProblemePageFull",
        component: () => import("src/pages/SignalerProblemePage.vue"),
      },
    ],
  },
  {
    path: "/payment",
    component: () => import("layouts/FullLayout.vue"),
    children: [
      {
        path: "status",
        name: "payment-status",
        component: () => import("pages/paymentStatusPage.vue"),
      },
    ],
  },
  /* {
    path: "hote-confirme",
    component: () => import("pages/hoteConfirme.vue"),
  }, */
  {
    path: "/chat/:reservationId",
    component: () => import("layouts/FullLayout.vue"),
    children: [
      {
        path: "",
        name: "chat",
        component: () => import("pages/ChatPage.vue"),
      },
    ],
  },
  {
    path: "/suppression",
    component: () => import("pages/suppressionPage.vue"),
  },
  {
    path: "/confirm-account-deletion",
    component: () => import("pages/ConfirmDeletionPage.vue"),
  },
  {
    path: "/email-verify",
    component: () => import("pages/EmailVerifyPage.vue"),
  },
  {
    path: "/categorie/:id",
    component: () => import("src/layouts/FullLayout.vue"),
    children: [
      {
        path: "",
        name: "categorie-logements",
        component: () => import("src/pages/CategorieLogementsPage.vue"),
        props: true,
      },
    ],
  },
  {
    path: "/mobile/categorie/:id",
    component: () => import("src/layouts/FullLayout.vue"),
    children: [
      {
        path: "",
        name: "mobile-categorie-logements",
        component: () => import("src/pages/CategorieLogementsPage.vue"),
        props: true,
      },
    ],
  },
  {
    path: "/marketplace/offres/:id",
    component: () => import("src/layouts/FullLayout.vue"),
    children: [
      {
        path: "",
        name: "share-offre",
        component: () => import("src/pages/ShareOffrePage.vue"),
        props: true,
      },
    ],
  },
  {
    path: "/marketplace/prestataire/:id",
    component: () => import("src/layouts/FullLayout.vue"),
    children: [
      {
        path: "",
        name: "share-prestataire",
        component: () => import("src/pages/SharePrestatairePage.vue"),
        props: true,
      },
    ],
  },
  {
    path: "/network/session/:id",
    component: () => import("src/layouts/FullLayout.vue"),
    children: [
      {
        path: "",
        name: "share-session",
        component: () => import("src/pages/ShareSessionPage.vue"),
        props: true,
      },
    ],
  },
  // Always leave this as last one,
  // but you can also remove it
  {
    path: "/:catchAll(.*)*",
    component: () => import("pages/ErrorNotFound.vue"),
  },
];

export default routes;
